<template>
  <div :class="{
    'cp-main__container': isCustomerPortal && isSessionUserKnown,
    'main__container': session && !session.is_legacy,
    'no__subtitle': session && !session.subtitle && session.is_legacy
  }">
    <Breadcrumbs v-if="showBreadcrumbs" :breadcrumbs-info="breadcrumbs" :ignore-current="true"></Breadcrumbs>
    <b-modal
      :visible="!!warning"
      id="warning"
      centered
      hide-footer
      hide-header
      size="lg"
      @hide="onCloseWarning()"
    >
      <b-row class="alert-messages">
        <div class="alert-container" v-bind:class="{'mt-0': !session}">
          <div>{{warning}}</div>
        </div>
      </b-row>
      <b-link block class="centered-block close-button" variant="secondary" @click="closeWarning()">&times;</b-link>
    </b-modal>
    <b-modal
      :visible="!!callCenterPopupContent"
      id="call-center-popup"
      centered
      hide-footer
      hide-header
      size="lg"
      @hide="onCloseCallCenterPopup()"
    >
      <template v-if="!!callCenterPopupContent">
        <div v-html="callCenterPopupContent"></div>
      </template>
      <b-link block class="centered-block close-button" variant="secondary" @click="closeCallCenterPopup()">&times;</b-link>
    </b-modal>
    <div v-if="showHeading" class="cp-main__title">
      <p class="container"><span v-html="session.welcome_message"></span></p>
    </div>
    <template v-if="!callCenterPopupContent">
      <b-modal
        :visible="isLifelineActive"
        id="lifeline"
        centered
        hide-footer
        hide-header
        size="lg"
        @hide="onCloseLifeline"
      >
        <template v-if="isLifelineActive">
          <ProcessingGif :classes="'popup-processing-image'" />
          <template v-if="!isLoading">
            <b-row v-if="showError" class="alert-messages">
              <b-col>
                <b-alert show variant="danger">{{errorMessage}}</b-alert>
              </b-col>
            </b-row>
            <Session :session="session" @init="init"></Session>
          </template>
        </template>
        <b-link block class="centered-block close-button" variant="secondary" @click="close()">&times;</b-link>
      </b-modal>
      <template v-if="!isLifelineActive">
        <template v-if="session && session.is_popup && (drawState > 1)">
          <b-modal
            id="session-popup"
            centered
            hide-footer
            hide-header
            no-close-on-backdrop
            no-close-on-esc
            size="xl"
            :modal-class="{'confirmation-popup': !session.is_legacy && isConfirmationPopup}"
            :visible="session && session.is_popup"
          >
            <ProcessingGif :classes="'popup-processing-image'" :processing="isPopupLoading" />
            <b-row v-if="!isPopupLoading" class="alert-messages">
              <Alert></Alert>
            </b-row>
            <div :class="{'d-none': isPopupLoading}">
              <Session :session="popupSession" @ready="sessionReady"></Session>
            </div>
          </b-modal>
          <Session v-if="drawState > 0" :session="session" @ready="sessionReady"></Session>
        </template>
        <template v-else-if="drawState > 0">
          <Session :session="session" @ready="sessionReady"></Session>
        </template>
      </template>
    </template>
  </div>
</template>

<script>
import {mapState, mapGetters} from 'vuex'
import {InputType, SessionResult, CustomerPortalInputType, ShowBreadcrumbs} from '@/constants'
import {getErrorMessage, closeLifeline} from '@/utils'
import axios from 'axios'

const Alert = () => import('@/components/Alert')
const ProcessingGif = () => import('@/components/ProcessingGif')
const Breadcrumbs = () => import('@/components/Breadcrumbs')
const Session = () => import('@/components/Session')

export default {
  name: 'SessionContainer',
  components: {
    Alert,
    ProcessingGif,
    Breadcrumbs,
    Session
  },
  data: function() {
    return {
      breadcrumbs: null,
      callCenterPopupContent: null,
      drawState: 0
    }
  },
  computed: {
    ...mapState(['session', 'isLoading', 'error', 'popupSession', 'isPopupLoading', 'messages', 'campaign', 'warning']),
    ...mapGetters(['isLifelineActive', 'isSessionUserKnown', 'isMainMenu']),
    showError: function() {
      return this.error && (this.session.lifeline_status === SessionResult.LifelineError)
    },
    errorMessage: function() {
      return getErrorMessage(this.error)
    },
    isConfirmationPopup: function() {
      return this.session && this.session.step_type === InputType.Confirmation
    },
    isCustomerPortal: function() {
      if (this.session) {
        if (Object.values(CustomerPortalInputType).indexOf(this.session.step_type) > -1) {
          return true
        }

        if (this.session.valid_inputs && this.session.valid_inputs.length > 0) {
          if (Object.values(CustomerPortalInputType).indexOf(this.session.valid_inputs[0].type) > -1) {
            return true
          }
        }
      }

      return false
    },
    showBreadcrumbs: function() {
      if (!this.isMainMenu && !this.isCustomerPortal && this.session && this.session.campaign &&
        (this.session.campaign.show_breadcrumbs !== ShowBreadcrumbs.Never) &&
        !this.needsCustomerMatch && !this.needsCustomerVerification) {
        if (this.isCustomerInfo || this.isPaymentInfo || this.isUpdateInfoStep ||
          (this.stepStack && this.stepStack.length > 0)) {
          return (this.session.campaign.show_breadcrumbs === ShowBreadcrumbs.Always) || this.session.can_undo
        }
      }

      return false
    },
    showHeading() {
      return this.isSessionUserKnown && this.isCustomerPortal && this.campaign.portal_settings &&
        this.session && this.session.welcome_message
    }
  },
  methods: {
    close: function() {
      this.$bvModal.hide('lifeline')
    },
    onCloseLifeline: function() {
      this.$store.commit('reset_lifeline')
      this.$store.commit('clear_messages')
      this.$store.commit('reset_idle_timer')

      if (this.session && this.session.can_exit_lifeline && !this.session.show_lifeline_contact) {
        this.$store.commit('busy')
        closeLifeline()
      }
    },
    closeCallCenterPopup: function() {
      this.$bvModal.hide('call-center-popup')
    },
    onCloseCallCenterPopup: function() {
      this.callCenterPopupContent = null

      if (this.isInputActionStatus(this.session && this.session.is_popup ? this.popupSession : this.session)) {
        this.submit()
      }
    },
    closeWarning: function() {
      this.$bvModal.hide('warning')
    },
    onCloseWarning: function() {
      // refresh the session data
      axios.get('/sessions/')
        .then(resp => {
          this.$store.commit('session', resp.data)
        })
        .catch(err => {
          this.$store.commit('critical_error', err)
        })
        .finally(() => this.init())
    },
    populateBreadcrumbs: function() {
      if (this.showBreadcrumbs) {
        // Always start with the Main Menu
        const breadcrumbs = [
          {label: 'Main Menu', path: {name: 'session'}, home: true}
        ]
        const crumbIndexMap = {}

        // Get the step stack from the store
        const stepStack = this.stepStack

        // Add breadcrumbs for each step in the stack
        if (stepStack && stepStack.length > 0) {
          stepStack.forEach((stepInfo, index) => {
            // For each step in the stack, create a breadcrumb that will undo the appropriate number of times
            const isCurrent = index === stepStack.length - 1
            const crumb = {
              label: stepInfo.name,
              slug: stepInfo.slug,
              undoCount: stepStack.length - index - 1,
              path: { name: 'session', params: { step: stepInfo.id } },
              back: (undoCount) => this.processUndo(undoCount),
              current: isCurrent
            }

            if (stepInfo.id in crumbIndexMap) {
              // crumb already exists, so update it and remove anything after it
              breadcrumbs.splice(crumbIndexMap[stepInfo.id], Number.POSITIVE_INFINITY, crumb)
            } else {
              // new crumb, so add it
              crumbIndexMap[stepInfo.id] = breadcrumbs.length
              breadcrumbs.push(crumb)
            }
          })
        } else if (this.session && this.session.step_name && !this.isMainMenu) {
          // Fallback for current step if stack is empty
          breadcrumbs.push({
            label: this.session.step_name,
            path: { name: 'session', params: { step: this.session.step_id } },
            home: false,
            current: true
          })
        }

        this.breadcrumbs = breadcrumbs
      }
    },
    init: function() {
      console.log('init session container')
      this.$store.commit('clear_warning')
      this.drawState = 0

      if (this.session) {
        this.callCenterPopupContent = this.session.call_center_popup
        this.populateBreadcrumbs()
      }
      this.sessionReady()
    },
    sessionReady: function() {
      const _this = this
      setTimeout(() => { _this.drawState++ }, 0)
    }
  },
  created() {
    this.init()
    this.$root.$on('refresh-session', () => {
      this.init()
    })
  },
  beforeDestroy() {
    this.$root.$emit('toggle-processing-gifs', null)
    this.$root.$off('refresh-session')
  }
}
</script>

<style scoped lang="less">
  #session-popup {
    .modal-body {
      [class*='col'] {
        padding-left: 2px;
        padding-right: 2px;
      }
    }
  }
</style>
